import React, { useState, useEffect } from 'react';

import { Typography, Button, Input, Tag, message, Collapse } from 'antd';
import { CheckCircleOutlined, EditOutlined, InfoCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { styles } from '../review-panel.styles';
import { useUpdateReviewBanner } from '../../../../hooks/banners/review/use-update-review-banner';
import { parseSubtitlesToText, rebuildSRT } from '../../../../utils';
import { ReviewField } from '../../../../types/banner/review-fields';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Panel } = Collapse;

interface SubtitlesReviewCardProps {
  bannerId: string;
  subtitles: ReviewField;
  onSubmitted?: () => void;
}

export const SubtitlesReviewCard: React.FC<SubtitlesReviewCardProps> = ({ bannerId, subtitles, onSubmitted }) => {
  const originalText = parseSubtitlesToText(subtitles.content);
  const [editedContent, setEditedContent] = useState(originalText);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { updateReviewBanner } = useUpdateReviewBanner();

  // Detectar cambios
  useEffect(() => {
    setHasChanges(editedContent !== originalText);
  }, [editedContent, originalText]);

  const handleSubmit = React.useCallback(async () => {
    setIsSubmitting(true);

    try {
      const finalSRT = rebuildSRT(subtitles.content, editedContent);
      const result = await updateReviewBanner(bannerId, 'subtitles', finalSRT);

      if (result) {
        message.success('Subtítulos confirmados correctamente');
        onSubmitted?.();
      } else {
        throw new Error('No response from server');
      }
    } catch (error) {
      console.error('Error submitting subtitles:', error);
      message.error('Error al confirmar subtítulos');
    } finally {
      setIsSubmitting(false);
    }
  }, [bannerId, editedContent, onSubmitted, subtitles.content, updateReviewBanner]);

  return (
    <Collapse
      defaultActiveKey={['subtitles']}
      expandIconPosition='right'
      ghost
      style={{
        background: 'white',
        border: '2px solid #f59e0b',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(245, 158, 11, 0.12)'
      }}
    >
      <Panel
        key='subtitles'
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={styles.cardIcon}>
                <FileTextOutlined style={{ fontSize: 24, color: '#f59e0b' }} />
              </div>
              <Title level={5} style={{ margin: 0, color: '#92400e' }}>
                Subtítulos
              </Title>
            </div>
            <Tag style={styles.statusBadge}>PENDIENTE</Tag>
          </div>
        }
        style={{
          borderRadius: 16
        }}
        className='subtitle-panel'
      >
        {/* Editor */}
        <div style={styles.editorSection}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <EditOutlined style={{ color: '#64748b', fontSize: 14 }} />
            <Text strong style={{ fontSize: 14, color: '#334155' }}>
              Contenido de subtítulos
            </Text>
          </div>

          <TextArea
            value={editedContent}
            onChange={e => setEditedContent(e.target.value)}
            placeholder='Edita el texto de los subtítulos aquí...'
            autoSize={{ minRows: 8, maxRows: 16 }}
            style={styles.textarea}
            disabled={isSubmitting}
          />

          <div style={styles.editorNote}>
            <InfoCircleOutlined style={{ color: '#64748b', fontSize: 14, marginTop: 2 }} />
            <Text style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <strong>Nota:</strong> Solo podes editar el texto de los subtítulos. Los tiempos de aparición se
              mantendrán según lo generado originalmente.
            </Text>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.cardFooter}>
          <Button
            type='primary'
            size='large'
            icon={<CheckCircleOutlined />}
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            style={styles.confirmButton}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.transform = 'scale(1.03)';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.transform = 'scale(1)';
            }}
          >
            {hasChanges ? 'Confirmar cambios' : 'Confirmar subtítulos'}
          </Button>
        </div>
      </Panel>
    </Collapse>
  );
};
